import React from 'react';
import moment from 'moment-timezone';
import {
  AlertTriangle,
  CalendarClock,
  CalendarPlus,
  FileSignature,
  Hourglass,
  MessageSquare,
  PartyPopper,
  RotateCcw,
  Zap,
} from 'lucide-react';

import { STRIPE, fillFor } from '../slaUtils';
import { cn } from '../../../../lib/utils';

// THE SIGNUP PATH's rules (Nic 2026-09-24, simplified 2026-09-25, 2026-10-06),
// shared by Signing Up's desk, the side panel and the board's tab count.
// Signing Up is only for people who did NOT sign on the first call:
//   Lead > Call > Sign up            (never lands here: Signed today)
//   Lead > Call > Book a follow-up   (Follow-up)
//   Lead > Call > Nurture or Drop    (off the board)
// Where they are, left to right:
//   FOLLOW-UP          Book or sign up | Booked
//   SERVICE AGREEMENT  Signup link sent | Account made | Agreement sent
//   then Signed today.
// Book or sign up = no follow-up on the calendar (re-engaged from Nurture,
// a no-show to rebook): book one or send the agreement. Booked = a
// follow-up today or ahead (booked within the next 2 days on the call). A
// person only moves RIGHT, on facts, and each spot auto-nurtures past its
// checkpoint (server/lib/potentialClients.js). A row opens the Workspace.
// Until 2026-10-21 these were lanes of cards (PotentialClientsView); now
// they're one list on the desk (SigningUpDesk.js).

export const stageOf = (run) => run.pipelineStage || 'in_conversation';

export const STATIONS = [
  // Follow-up splits by the calendar, not the stage (Nic 2026-10-06:
  // Booked shows only real meetings): a follow-up today or ahead is Booked;
  // anyone else in Follow-up still needs one (re-engaged, a no-show to
  // rebook, or a call that ended with nothing booked).
  {
    key: 'tobook',
    group: 'followup',
    name: 'Book or sign up',
    stages: [],
    icon: RotateCcw,
    job: 'Book a follow-up or send the agreement today.',
    moves: 'A follow-up is booked or the signup starts',
  },
  {
    key: 'booked',
    group: 'followup',
    name: 'Booked',
    stages: [],
    icon: CalendarClock,
    job: 'Sign them up at the follow-up.',
    moves: 'They sign up at the follow-up',
  },
  {
    key: 'link',
    group: 'agreement',
    name: 'Signup link sent',
    stages: ['signup_link_sent'],
    job: 'They create their account (2 days).',
    moves: 'They create their account',
  },
  {
    key: 'account',
    group: 'agreement',
    name: 'Account made',
    stages: ['account_made'],
    job: 'Confirm their facts, send the agreement.',
    moves: 'The agreement is sent',
  },
  {
    key: 'agreement',
    group: 'agreement',
    name: 'Agreement sent',
    stages: ['agreement_out'],
    job: 'They sign (3 days).',
    moves: 'They sign',
  },
];

// ---- The colors ----
const AGE = {
  blue: {
    label: 'On track',
    stripe: '#60A5FA', // blue-400
    fill: fillFor(STRIPE.upcoming), // blue-500
    pill: 'bg-blue-50 text-blue-700 dark:!bg-blue-500/15 dark:text-blue-300',
    dot: 'bg-blue-500',
  },
  yellow: {
    label: '1 day left',
    stripe: STRIPE.due,
    fill: fillFor(STRIPE.due),
    pill: 'bg-amber-50 text-amber-700 dark:!bg-amber-500/15 dark:text-amber-300',
    dot: 'bg-amber-500',
  },
  red: {
    label: 'Last day',
    stripe: STRIPE.overdue,
    fill: fillFor(STRIPE.overdue),
    pill: 'bg-red-50 text-red-700 dark:!bg-red-500/15 dark:text-red-300',
    dot: 'bg-red-500',
  },
};
// The card's color: the checkpoint's time left (the server's call).
export const toneOf = (run) => run?.checkpoint?.tone || 'blue';
// For the side panel's header pill, keyed by toneOf().
export const AGE_PILL = {
  blue: AGE.blue.pill,
  yellow: AGE.yellow.pill,
  red: AGE.red.pill,
};

// One color law for the cards, Making Contact's own (Nic 2026-10-07): blue
// = nothing to do (a meeting ahead, or their move after our nudge), yellow =
// act now (book or sign up, nudge), red = they're waiting on us (Account
// made, overdue). No green: a signed card is in Signed today. The colors are
// Making Contact's stripes and solid fills (slaUtils).
export const CARD_TONE = {
  blue: {
    fill: fillFor(STRIPE.upcoming),
    edge: STRIPE.upcoming,
    label: 'Waiting on them',
  },
  yellow: { fill: fillFor(STRIPE.due), edge: STRIPE.due, label: 'Act now' },
  red: {
    fill: fillFor(STRIPE.overdue),
    edge: STRIPE.overdue,
    label: 'Waiting on us',
  },
};
export const POTENTIAL_LEGEND = ['blue', 'yellow', 'red'].map((t) => ({
  color: CARD_TONE[t].edge,
  fill: CARD_TONE[t].fill,
  label: CARD_TONE[t].label,
}));

// What the card's hover says about its checkpoint.
export const checkpointLine = (run, closedUntil = null) => {
  const w = dayWords(closedUntil);
  const cp = run?.checkpoint;
  if (!cp) return '';
  if (cp.held) return `${cp.need}. On hold while a meeting is ahead.`;
  if (cp.overdue) {
    return `Overdue: send the agreement now. ${cp.daysLate} business day${
      cp.daysLate === 1 ? '' : 's'
    } late. They are waiting on us, so they never go to Nurture.`;
  }
  if (cp.noNurture && cp.left <= 0) return `${cp.need} ${w.today}.`;
  if (cp.left <= 0) return `${cp.need} ${w.today}, or they move to Nurture ${w.tonight}.`;
  return `${cp.need}: ${cp.left} business day${cp.left === 1 ? '' : 's'} left after today.`;
};

// What's needed (Nic 2026-09-30): the one next thing on Signing Up, in the
// words the side panel shows. A meeting still ahead IS the next thing; otherwise the
// checkpoint's need, how long is left, and how the last meeting went.
//   { held, meeting: {at, what, host}, need, due, pill, tone, prev, station }
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
// Where today's meeting stands: Upcoming, In 12 min, Happening now, Met.
const heldPill = (next, now) => {
  const t = new Date(now || Date.now()).getTime();
  const start = new Date(next.startTime).getTime();
  const end = next.endTime
    ? new Date(next.endTime).getTime()
    : start + 30 * 60 * 1000;
  if (t < start) {
    const mins = Math.ceil((start - t) / 60000);
    return mins <= 15 ? `In ${mins} min` : 'Upcoming';
  }
  return t < end ? 'Happening now' : 'Met';
};
export const needOf = (run, now, closedUntil = null) => {
  const w = dayWords(closedUntil);
  const cp = run?.checkpoint || {};
  const next = run?.meetingTrail?.next || run?.booking || null;
  const held = !!cp.held && !!next?.startTime;
  const left = cp.left ?? 2;
  // The meeting that just happened: today's finished one still rides as
  // `next` until the day ends; otherwise the one before it.
  const nextIsOver =
    !!next?.startTime &&
    next.status !== 'canceled' &&
    new Date(next.startTime) <= new Date(now || Date.now());
  const nextMissed =
    next?.status === 'no_show' ||
    !!next?.noShowAt ||
    next?.outcome === 'no_show';
  const prev = held
    ? null
    : nextIsOver
      ? {
          at: next.startTime,
          outcome: nextMissed ? 'missed' : 'met',
          hostName: next.hostName || null,
        }
      : run?.meetingTrail?.previous || null;
  return {
    station: STATION_NAME(run),
    held,
    meeting: held
      ? {
          at: next.startTime,
          what: next.eventTypeName || 'Meeting',
          host: next.hostName || null,
        }
      : null,
    need: cp.need || 'Next step',
    due: cp.overdue
      ? `${plural(cp.daysLate || 1, 'business day')} late. They are waiting on us.`
      : left <= 0
        ? cp.noNurture
          ? `Due ${w.today}.`
          : `Due ${w.today}, or they move to Nurture ${w.tonight}.`
        : `${plural(left, 'business day')} left after today.`,
    // A meeting holds the clock all day, so the pill follows the time (Nic
    // 2026-10-05: it said Upcoming during and after the meeting).
    pill: held
      ? heldPill(next, now)
      : cp.overdue
        ? 'Overdue'
        : left <= 0
          ? 'Last day'
          : `${plural(left, 'day')} left`,
    // 'meeting' (teal) while one is ahead, else the card's own color.
    tone: held ? 'meeting' : toneOf(run),
    overdue: !!cp.overdue,
    prev,
  };
};

// ---- Meetings ----
const clock = (at) => moment(at).local().format('h:mm A');
export const meetingWhen = (at, now) => {
  const m = moment(at).local();
  const n = moment(now).local();
  if (m.isSame(n, 'day')) return clock(at);
  if (m.isSame(n.clone().add(1, 'day'), 'day')) return `Tomorrow ${clock(at)}`;
  return m.format('ddd h:mm A');
};

// Who the meeting is with, when it is not the card's owner (Nic 2026-09-29:
// meetings booked before Signing Up stay with whoever they were booked with).
// Only the initial move produces these (new bookings go on the owner's own
// calendar), so the card marks them as legacy with a history icon.
const hostOf = (run, booking = run.booking) => {
  const host = String(booking?.hostName || '').trim();
  if (!host) return null;
  const owner = String(run.ownerName || '')
    .trim()
    .toLowerCase();
  const h = host.toLowerCase();
  // Calendly often stores only a first name ("Chris" for Chris Rose), so a
  // first-name match is the owner too (Nic 2026-10-06: every card wore the
  // "booked with someone else" mark).
  const same = h === owner || (!!owner && h === owner.split(/\s+/)[0]);
  return same ? null : host.split(/\s+/)[0];
};

// The lead's meeting, if one is on the books or just happened.
//   { upcoming, today, soon, at, host } or { past, at, host }
// Nobody is asked how it went (Nic 2026-09-29): a meeting nobody marks a
// no-show happened, and the sweep settles it after its day. A no-show is
// marked in Calendly, and its webhook moves the card.
// The meeting shown is the SOONEST one still to come, or today's until its
// day is over (the board's meetingTrail, Nic 2026-09-29). The trail also
// carries how the meeting before it went and any booked after it.
export const meetingOf = (run, now) => {
  const trail = run.meetingTrail;
  const b = trail ? trail.next : run.booking;
  if (!b || b.status !== 'active' || !b.startTime || b.outcome) return null;
  const start = moment(b.startTime);
  const end = b.endTime ? moment(b.endTime) : start.clone().add(30, 'minutes');
  const host = hostOf(run, b);
  const extra = {
    previous: trail?.previous || null,
    later: trail?.later || [],
    // Booked in Calendly before launch, or in the app (Nic 2026-10-27: the
    // desk tags the old ones). kind: 'phone' | 'video' for in-app meetings.
    provider: b.provider || 'calendly',
    kind: b.kind || null,
  };
  if (moment(now).isBefore(end)) {
    return {
      upcoming: true,
      at: b.startTime,
      host,
      today: start.clone().local().isSame(moment(now).local(), 'day'),
      soon: start.diff(moment(now), 'minutes') <= 15,
      ...extra,
    };
  }
  return { past: true, at: b.startTime, host, ...extra };
};

const FOLLOWUP_STAGES = [
  're_engaged',
  'in_conversation',
  'meeting_link_sent',
  'meeting_booked',
  'missed_meeting',
];
export const stationOf = (run) => {
  if (FOLLOWUP_STAGES.includes(stageOf(run))) {
    return run?.checkpoint?.held ? 'booked' : 'tobook';
  }
  return STATIONS.find((s) => s.stages.includes(stageOf(run)))?.key || 'tobook';
};
// The column a run sits in, by name (the side panel says "Link sent").
export const STATION_NAME = (run) =>
  STATIONS.find((s) => s.key === stationOf(run))?.name || 'Book or sign up';

// ---- The meeting mark (Nic 2026-09-29: less on the card, all on hover) ----
// A tiny tear-off calendar (month band over the day number), fitting the
// card's single line; the time is in the hover. Today reads TODAY on the band. A
// meeting from the initial move (booked with someone else) gets an amber
// transfer mark (two arrows) right after the name. Everything else lives in the hover.
// The tile sits at the card's right edge, so it is in the same place on
// every row.
// Met = 30 minutes past the start and nobody marked a no-show (Nic
// 2026-09-29): a check on the tile, nothing else to read.
const MET_AFTER_MIN = 30;
export const isMet = (meeting, now) =>
  !!meeting.past &&
  moment(now).diff(moment(meeting.at), 'minutes') >= MET_AFTER_MIN;
const fmtWhen = (at) => moment(at).local().format('ddd, MMM D [at] h:mm A');
const meetingTitle = (meeting, now) => {
  const when = fmtWhen(meeting.at);
  // The legacy note lives on the transfer icon by the name, not here.
  const lines = [isMet(meeting, now) ? `Met · ${when}` : `Meeting · ${when}`];
  if (meeting.previous) {
    const day = moment(meeting.previous.at).local().format('MMM D');
    lines.push(
      meeting.previous.outcome === 'missed'
        ? `Before: missed the meeting on ${day}`
        : `Before: met on ${day}`,
    );
  }
  (meeting.later || []).forEach((l) =>
    lines.push(`Also booked · ${fmtWhen(l.at)}`),
  );
  return lines.join('\n');
};

// The day a checkpoint runs out, in words: "tomorrow" or "Thu".
const byDay = (cp, now) => {
  if (!cp?.dueBy) return cp?.left === 1 ? 'tomorrow' : 'soon';
  const due = moment(cp.dueBy).local();
  const n = moment(now).local();
  if (due.isSame(n.clone().add(1, 'day'), 'day')) return 'tomorrow';
  return due.format('ddd');
};
// What the rep does at the meeting, for the desk's Next column: sign them
// up on the call; once it's time, join (video) or call (phone).
const meetingDeskText = (meeting, now) => {
  const mins = Math.ceil(moment(meeting.at).diff(moment(now), 'minutes', true));
  const verb = meeting.kind === 'video' ? 'Join' : 'Call them';
  if (mins <= 0) return `${verb} now`;
  if (mins <= 15) return `${verb} in ${mins} min`;
  return 'Sign them up on the call';
};
const meetingChipText = (meeting, now) => {
  const mins = Math.ceil(moment(meeting.at).diff(moment(now), 'minutes', true));
  if (mins <= 0) return 'Happening now';
  if (mins <= 15) return `In ${mins} min`;
  const when = meetingWhen(meeting.at, now).replace(':00 ', ' ');
  return meeting.today ? `Today ${when}` : when;
};

/**
 * The one chip on a card (Nic 2026-10-06): what happens and when, in words.
 *   { text, kind, act, tone, title, icon, ourMove }
 *   act: due today (the tab and column counts). tone: the card's color
 *   (CARD_TONE). icon: the chip's mark in the info popover.
 *   kind: act (solid red card, the rep's move today) | meeting | warn |
 *         quiet | late (their move, last day) | ready
 */
// "today" and "tonight" on a closed day (Nic 2026-11-01): the counter only
// moves on working days, so a last-day card read "Book or sign up today"
// all weekend. closedUntil is the board's next opening (workCalendar
// .nextOpenAt) when today is closed, else null; the words then say the day
// the deadline really is ("by Mon", "Mon night").
export const dayWords = (closedUntil) => {
  if (!closedUntil) return { today: 'today', tonight: 'tonight' };
  const d = moment(closedUntil).local().format('ddd');
  return { today: `by ${d}`, tonight: `${d} night` };
};

export const chipOf = (run, now, { ready = false, closedUntil = null } = {}) => {
  const cp = run?.checkpoint || {};
  const station = stationOf(run);
  const meeting = meetingOf(run, now);
  const last = (cp.left ?? 2) <= 0;
  const by = byDay(cp, now);
  const w = dayWords(closedUntil);
  if (ready) {
    return {
      kind: 'ready',
      act: true,
      tone: 'red',
      icon: Zap,
      text: 'Details in',
      title:
        'They confirmed their details in their signup. Confirm the facts and send the agreement now.',
    };
  }
  if (cp.overdue) {
    return {
      kind: 'act',
      act: true,
      tone: 'red',
      icon: AlertTriangle,
      text: `Send agreement · ${cp.daysLate || 1}d late`,
      title:
        'They are waiting on us, so they never go to Nurture. Send it now.',
    };
  }
  if (meeting?.upcoming && cp.held) {
    const rebook = !!run?.pipelineStageMeta?.rebook;
    return {
      kind: 'meeting',
      tone: 'blue',
      icon: CalendarClock,
      text: `${rebook ? 'Rebooked · ' : ''}${meetingChipText(meeting, now)}`,
      // The desk's Next column (Nic 2026-10-27): the Meeting column already
      // says when, so Next says what the rep does on it.
      deskText: `${rebook ? 'Rebooked · ' : ''}${meetingDeskText(meeting, now)}`,
      title: `${meetingTitle(meeting, now)}${rebook ? '\nThis is their one rebook: missing it sends them to Nurture.' : ''}`,
    };
  }
  if (stageOf(run) === 're_engaged' && station === 'tobook') {
    return {
      kind: 'act',
      act: true,
      tone: 'yellow',
      icon: CalendarPlus,
      text: `Re-engaged · book or sign up ${w.today}`,
      title: `Re-engaged: book a follow-up or sign them up ${w.today}, or they go back to Nurture ${w.tonight}.`,
    };
  }
  if (station === 'tobook' || station === 'booked') {
    if (cp.key === 'meeting_missed') {
      return last
        ? {
            kind: 'act',
            act: true,
            tone: 'yellow',
            icon: CalendarPlus,
            text: `No-show · rebook or sign up ${w.today}`,
            title: `Rebook their follow-up or sign them up ${w.today}, or they go to Nurture ${w.tonight}. One rebook only.`,
          }
        : {
            kind: 'warn',
            tone: 'yellow',
            icon: CalendarPlus,
            text: `No-show · rebook or sign up by ${by}`,
            title:
              'Rebook their follow-up or sign them up. One rebook only. Neither in time = Nurture.',
          };
    }
    if (cp.key === 'meeting_missed_again') {
      return {
        kind: 'act',
        act: true,
        tone: 'yellow',
        // On us, not them (Nic 2026-10-07): no Nudge.
        ourMove: true,
        icon: FileSignature,
        text: `Missed twice · Nurture ${w.tonight}`,
        title: `They missed their rebooked follow-up. Sign them up ${w.today}, or they go to Nurture ${w.tonight}.`,
      };
    }
    if (cp.key === 'meeting_met') {
      return {
        kind: 'act',
        act: true,
        tone: 'yellow',
        // The follow-up happened (Nic 2026-10-07): sign them up, nurture or
        // drop. On us, so no Nudge.
        ourMove: true,
        icon: FileSignature,
        text: `Met · sign up ${w.today}`,
        title: `They met for the follow-up but didn't sign up. Sign them up ${w.today}, or they go to Nurture ${w.tonight}.`,
      };
    }
    return {
      kind: 'act',
      act: true,
      tone: 'yellow',
      icon: CalendarPlus,
      text: `Book or sign up ${w.today}`,
      title: `No follow-up booked. Book one or sign them up ${w.today}, or they go to Nurture ${w.tonight}. We'll alert you if they ever reach back out.`,
    };
  }
  if (station === 'account') {
    return last
      ? {
          kind: 'act',
          act: true,
          tone: 'red',
          icon: FileSignature,
          text: `Send agreement ${w.today}`,
          title: `Confirm their facts and send the agreement ${w.today}.`,
        }
      : {
          kind: 'warn',
          tone: 'red',
          icon: FileSignature,
          text: `Send agreement by ${by}`,
          title: 'Confirm their facts and send the agreement.',
        };
  }
  // Their move: the link or the agreement (Nic 2026-10-06). The rep owes
  // ONE nudge text, and only on the LAST day (Nic 2026-10-07: the Nudge
  // button shows then, so the card asks for it then). Before that it is on
  // them: blue. "Fri night" = Friday is their last day; the sweep moves
  // them that night. A nudge never buys time.
  const nudgedAt = nudgedSince(run);
  const need = cp.need || 'Their next step';
  const when = last ? w.tonight : `${by} night`;
  if (!nudgedAt && last) {
    return {
      kind: 'act',
      act: true,
      tone: 'yellow',
      icon: MessageSquare,
      text: `Nudge ${w.today}`,
      title: `Their last day. Send them a nudge text ${w.today}. ${need} ${w.today}, or they go to Nurture ${w.tonight}.`,
    };
  }
  if (!nudgedAt) {
    return {
      kind: 'quiet',
      tone: 'blue',
      icon: Hourglass,
      text: `Waiting on them · Nurture ${when}`,
      title: `${need} by ${by}. Nothing to do yet: on ${by}, their last day, nudge them if they haven't. Otherwise they go to Nurture ${by} night.`,
    };
  }
  const sent = moment(nudgedAt).local().format('ddd h:mm A');
  return {
    kind: 'quiet',
    tone: 'blue',
    icon: Hourglass,
    text: `Nudged · Nurture ${when}`,
    title: `Nudge sent ${sent}. It's on them now: ${need.toLowerCase()} ${last ? w.today : `by ${by}`}, or they go to Nurture ${when}.`,
  };
};

// The last nudge text, if it went out after this column started.
export const nudgedSince = (run) => {
  const at = run?.lastNudgeAt;
  const start = run?.checkpointAt || run?.pipelineStageAt;
  if (!at) return null;
  return !start || new Date(at) >= new Date(start) ? at : null;
};

// The tab badge: cards that need the rep today, and the direct signups bucket.
// readyRunIds: Details in cards, which are act-today too (Nic 2026-10-06:
// the tab's number must match the red cards).
export const potentialNeedsCount = (
  runs = [],
  directSignupCount = 0,
  readyRunIds = [],
) => {
  const ready = new Set(readyRunIds);
  return (
    runs.filter(
      (r) => chipOf(r, Date.now(), { ready: ready.has(r.cadenceRunId) }).act,
    ).length + (directSignupCount > 0 ? 1 : 0)
  );
};

// The card's color is its chip's tone (CARD_TONE).
export const toneOfChip = (chip) => chip.tone || 'blue';

// Your move today first, then meetings today (soonest on top), then the
// least time left.
export const cardRank = (run, now) => {
  if (chipOf(run, now).act) return [0, run.checkpoint?.left ?? 0];
  const m = meetingOf(run, now);
  if (m?.upcoming && m.today) return [1, new Date(m.at).getTime()];
  return [2, run.checkpoint?.left ?? 99];
};

// One of today's wins, and where they went (Nic 2026-10-05): hot seated to
// a specialist, waiting on a hot seat (signed on a call; it reopens here if
// the Call Desk was closed), or signed with no call (onboarding's rotation
// gives them a specialist).
// Where each signup went (Nic 2026-10-23: status only, never a question;
// RingCentral says whether the hot seat picked up).
export const SignedTodayRow = ({ win: w }) => {
  const name =
    `${w.leadFirstName || ''} ${w.leadLastName || ''}`.trim() || 'Client';
  const who = w.onboardingFirstName || null;
  let status;
  if (w.hotSeated) {
    status =
      w.hotSeatConnected === false
        ? `With ${who || 'onboarding'}, calling them back`
        : w.hotSeatConnected === true
          ? `With ${who || 'onboarding'}`
          : `Sent to ${who || 'onboarding'}`;
  } else if (w.signedOnCall) {
    status = who ? `No hot seat. ${who} has them.` : 'No hot seat';
  } else {
    status = who ? `No call, so no hot seat. ${who} has them.` : 'No call, so no hot seat';
  }
  return (
    <div
      data-no-toggle
      className="rounded-lg bg-emerald-50 dark:!bg-emerald-500/10"
    >
      <div className="flex h-9 items-center gap-2.5 px-3">
        <PartyPopper
          size={14}
          className="shrink-0 text-emerald-600 dark:text-emerald-300"
        />
        <span className="min-w-0 flex-1 truncate text-[13px] font-bold text-emerald-900 dark:text-emerald-100">
          {name}
        </span>
        <span className="shrink-0 text-[11px] font-semibold tabular-nums text-emerald-700 dark:text-emerald-300">
          {clock(w.endedAt)}
        </span>
      </div>
      <div className="-mt-1 flex items-center gap-1.5 px-3 pb-2 pl-[2.1rem] text-[11px]">
        <span
          className={cn(
            'truncate',
            w.hotSeated ? 'font-semibold text-emerald-700 dark:text-emerald-300' : 'text-emerald-700/70 dark:text-emerald-300/70',
          )}
        >
          {status}
        </span>
      </div>
    </div>
  );
};
